import { Drawer, FormControl, Grid, Icon, InputLabel, MenuItem, Select, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { IFormTemplateManage } from "../../_common/models/formTemplate";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { formTemplateService } from "../../_services/formTemplate.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { UserStatusEnum } from "../../_common/enum/userStatus.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/fomTemplateType.enum";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";

export function FormTemplateManageDrawer(params: {
    emitOnSave: (id?: number) => void,
    emitOnClose: () => void
}) {
    const { formTemplateId } = useParams<{ formTemplateId: string }>();

    const [data, setData] = useState<IFormTemplateManage>(getResetData);
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [tabValue, setTabValue] = React.useState(0);

    let navigate = useNavigate();

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        navigate("");
        setTabValue(newValue);
    };


    useEffect(() => {
        if (formTemplateId) fetchData();
        else setData(getResetData);
    }, [formTemplateId]);

    function getResetData(): IFormTemplateManage {
        return {
            id: 0 || +(formTemplateId || 0),
            name: "",
            formTemplateTypeId: 0
        };
    }

    function fetchData() {
        setLoading(true);
        return formTemplateService
            .get(+(formTemplateId || 0))
            .then(
                (json) => {
                    if (json) {
                        setData(json);
                    }
                    else setData(getResetData);
                }
            )
            .finally(() => setLoading(false));
    }

    function closeModal() {
        params.emitOnClose();
    }

    function save(e: any) {
        e.preventDefault();
        setSaving(true);

        return formTemplateService
            .submit(data)
            .then(
                (id: number) => {
                    toastr.success("Saved successfully");
                    params.emitOnSave(id);
                },
                handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function disable() {
        setSaving(true);
        return formTemplateService
            .disable(+(formTemplateId || 0))
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.emitOnClose();
                },
                handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function submitForm() {
        setSaving(true);
        return formTemplateService
            .submitForm(+(formTemplateId || 0))
            .then(
                (id: number) => {
                    toastr.success("Form template submitted successfully");
                    params.emitOnSave(id);
                },
                handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function duplicateForm() {
        setSaving(true);
        return formTemplateService
            .duplicateForm(+(formTemplateId || 0))
            .then(
                (id: number) => {
                    toastr.success("Form template duplicated successfully");
                    params.emitOnSave(id);
                },
                handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function handleChange(event: any) {
        setData((_data) => ({ ..._data, [event.target.name]: event.target.value }))
    };

    function onError(event: any) {
        toastr.error("Please verify that all fields have correct input");
    }

    function getEnumDropdown(label: string, name: string) {
        var type = FormTemplateTypeEnum;
        const values = Object.values(type);

        var menuItems = values.filter((v) => !isNaN(Number(v))).map((value, index) =>
            <MenuItem value={value}>{type[Number(value)]}</MenuItem>
        );

        return <FormControl fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
                label={label}
                name={name}
                value={data[name]}
                placeholder={label}
                onChange={handleChange}
                variant="outlined">
                {menuItems}
            </Select>
        </FormControl>
    }

    return (
        <Drawer
            anchor={"right"}
            open={true}>

            <ValidatorForm onError={onError} onSubmit={save} noValidate>

                <DrawerHeader name={(!!formTemplateId) ? "Update Form Template" : "Create Form Template"} onClose={closeModal} />

                {(+(formTemplateId || 0) > 0) &&
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="simple tabs example">
                        <Tab label="Template" />
                        <Tab label="Sections" />
                    </Tabs>
                }

                {(tabValue === 0) &&
                    <>
                        <Grid container spacing={1} sx={useDrawerStyles.container}>
                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    label="Name"
                                    value={data.name}
                                    name="name"
                                    required
                                    fullWidth
                                    disabled={data.formStatusId !== FormStatusEnum.New && data.formStatusId !== FormStatusEnum.InProcess && data.id > 0}
                                    validators={['required']}
                                    errorMessages={['This field is required']}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {getEnumDropdown("Form Template Type", "formTemplateTypeId")}
                            </Grid>

                            {data.formTemplateTypeId == FormTemplateTypeEnum.Questionnaire &&
                                <Grid item xs={12}>
                                    <TextValidatorHcs
                                        label="Questionnaire Success % Required"
                                        value={data.questionsSuccessPercent}
                                        name="questionsSuccessPercent"
                                        required
                                        type="number"
                                        min="0"
                                        max="100"
                                        fullWidth
                                        validators={['required', 'minNumber:1', 'maxNumber:100']}
                                        errorMessages={['This field is required', 'Cannot be less than 1% success rate', 'Cannot be more than 100% success rate']}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            }

                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            {(data.id == 0 || data.formStatusId === FormStatusEnum.New || data.formStatusId === FormStatusEnum.InProcess) &&
                                <Grid item xs={data.id == 0 ? 12 : (data.formStatusId === FormStatusEnum.New || data.formStatusId === FormStatusEnum.InProcess) ? 6 : 6}>
                                    <PrimaryButton
                                        type="submit"
                                        variant="save"
                                        disabled={isLoading || isSaving || (data.formStatusId !== FormStatusEnum.New && data.formStatusId !== FormStatusEnum.InProcess && data.id > 0)}>
                                        Save
                                    </PrimaryButton>
                                </Grid>
                            }



                            {/* {(data.id > 0) &&
                                <Grid item xs={data.formStatusId === FormStatusEnum.InProcess ? 3 : data.formStatusId === FormStatusEnum.New ? 4 : 6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="warning"
                                        disabled={isLoading || isSaving}
                                        onClick={duplicateForm}>

                                        <Icon>file_copy</Icon> Duplicate
                                    </PrimaryButton>
                                </Grid>
                            } */}

                            {(data.id > 0) &&
                                <Grid item xs={(data.formStatusId === FormStatusEnum.InProcess || data.formStatusId === FormStatusEnum.New) ? 6 : 6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="remove"
                                        disabled={isLoading || isSaving}
                                        onClick={disable}>
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }
                            <br />
                            <br />
                            {(data.formStatusId === FormStatusEnum.New || data.formStatusId === FormStatusEnum.InProcess) &&
                                <Grid item xs={12}>
                                    <PrimaryButton
                                        type="submit"
                                        variant="info"
                                        disabled={isLoading || isSaving}
                                        onClick={submitForm}>

                                        <Icon>upload</Icon> Approve Form Template and push LIVE
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>
                    </>
                }

            </ValidatorForm>

            {
                (tabValue === 1) &&
                <Outlet />
            }

        </Drawer >
    );
}